import { useState } from "react";
import { usePageContext } from "vike-react/usePageContext";
import Breadcrumbs from "@/components/seo/Breadcrumbs";

type Locale = "id" | "en";

const BREADCRUMB_ITEMS_ID = [
  { name: "Beranda", path: "/" },
  { name: "Privasi", path: "/privacy" },
];

const BREADCRUMB_ITEMS_EN = [
  { name: "Home", path: "/" },
  { name: "Privacy", path: "/privacy" },
];

export default function Page() {
  const pageContext = usePageContext();
  const site = pageContext.siteConfig;
  const [locale, setLocale] = useState<Locale>("id");

  const isID = locale === "id";
  const breadcrumbs = isID ? BREADCRUMB_ITEMS_ID : BREADCRUMB_ITEMS_EN;
  const siteName = site?.name ?? "Bisnisgarasi.com";

  return (
    <section className="bg-background py-16 md:py-24" lang={locale}>
      <div className="container-narrow">
        <Breadcrumbs items={breadcrumbs} className="mb-8" />

        <div className="flex flex-col gap-6 sm:flex-row sm:items-start sm:justify-between">
          <header>
            <p className="text-mono text-xs uppercase tracking-widest text-muted-foreground">
              // {isID ? "Kebijakan Privasi & Disclosure" : "Privacy & Disclosure"}
            </p>
            <h1 className="heading-display mt-3 text-4xl text-foreground sm:text-5xl">
              {isID ? "Privasi & Disclosure" : "Privacy & Disclosure"}
            </h1>
            <p className="mt-4 max-w-2xl text-muted-foreground">
              {isID
                ? "Halaman ini menjelaskan teknologi pelacakan yang kami gunakan untuk meningkatkan layanan. Tersedia dalam Bahasa Indonesia dan English."
                : "This page describes the tracking technologies we use to improve our services. Available in Bahasa Indonesia and English."}
            </p>
          </header>

          <div
            role="group"
            aria-label={isID ? "Pilih bahasa" : "Select language"}
            className="inline-flex shrink-0 items-center rounded-md border border-border bg-surface-elevated p-1"
          >
            <button
              type="button"
              onClick={() => setLocale("id")}
              aria-pressed={isID}
              className={`rounded-sm px-3 py-1.5 text-mono text-xs uppercase tracking-wider transition-colors ${
                isID
                  ? "bg-primary/15 text-primary"
                  : "text-muted-foreground hover:text-foreground"
              }`}
            >
              ID
            </button>
            <button
              type="button"
              onClick={() => setLocale("en")}
              aria-pressed={!isID}
              className={`rounded-sm px-3 py-1.5 text-mono text-xs uppercase tracking-wider transition-colors ${
                !isID
                  ? "bg-primary/15 text-primary"
                  : "text-muted-foreground hover:text-foreground"
              }`}
            >
              EN
            </button>
          </div>
        </div>

        <div className="mt-12 space-y-12">
          {isID ? <IndonesianContent siteName={siteName} /> : <EnglishContent siteName={siteName} />}
        </div>

        <p className="mt-16 border-t border-border/60 pt-6 text-xs text-muted-foreground">
          {isID
            ? `Terakhir diperbarui: ${new Date().toLocaleDateString("id-ID", {
                day: "numeric",
                month: "long",
                year: "numeric",
              })}.`
            : `Last updated: ${new Date().toLocaleDateString("en-GB", {
                day: "numeric",
                month: "long",
                year: "numeric",
              })}.`}
        </p>
      </div>
    </section>
  );
}

function SectionHeading({ index, children }: { index: string; children: React.ReactNode }) {
  return (
    <h2 className="heading-display flex items-baseline gap-3 text-2xl text-foreground sm:text-3xl">
      <span className="text-mono text-sm text-primary">{index}</span>
      <span>{children}</span>
    </h2>
  );
}

function Callout({ children }: { children: React.ReactNode }) {
  return (
    <blockquote className="rounded-md border-l-4 border-primary/60 bg-surface-elevated px-5 py-4 text-sm leading-relaxed text-foreground">
      {children}
    </blockquote>
  );
}

function IndonesianContent({ siteName }: { siteName: string }) {
  return (
    <>
      <article className="space-y-4">
        <SectionHeading index="01">Pengungkapan Penggunaan Situs</SectionHeading>
        <p className="text-muted-foreground">
          Kami menggunakan <strong className="text-foreground">Microsoft Clarity</strong> dan{" "}
          <strong className="text-foreground">Google Analytics</strong> untuk memahami cara
          pengunjung berinteraksi dengan situs ini, sehingga kami dapat terus meningkatkan
          kualitas produk dan layanan.
        </p>
        <Callout>
          Kami meningkatkan produk dan iklan kami dengan menggunakan Microsoft Clarity untuk
          melihat bagaimana Anda menggunakan situs web ini. Dengan menggunakan situs kami,
          Anda menyetujui bahwa kami dan Microsoft dapat mengumpulkan dan menggunakan data
          tersebut. Untuk informasi lebih lanjut, silakan baca{" "}
          <a
            href="https://privacy.microsoft.com/privacystatement"
            target="_blank"
            rel="noopener noreferrer"
            className="text-primary underline-offset-2 hover:underline"
          >
            Pernyataan Privasi Microsoft
          </a>
          .
        </Callout>
        <ul className="list-disc space-y-2 pl-5 text-sm text-muted-foreground">
          <li>Kami menggunakan Microsoft Clarity dan Google Analytics.</li>
          <li>
            Kami menangkap data perilaku — seperti klik, scroll, pergerakan kursor, dan
            durasi sesi — untuk tujuan analisis dan peningkatan pengalaman pengguna.
          </li>
          <li>
            Dengan menggunakan situs ini, pengguna menyetujui pengumpulan dan penggunaan data
            oleh {siteName} maupun Microsoft.
          </li>
        </ul>
      </article>

      <article className="space-y-4">
        <SectionHeading index="02">Kebijakan Privasi</SectionHeading>
        <p className="text-muted-foreground">
          Berikut adalah ringkasan bagaimana kami memperlakukan data Anda saat menggunakan
          layanan analitik pihak ketiga di {siteName}.
        </p>
        <Callout>
          Kami bermitra dengan Microsoft Clarity dan Microsoft Advertising untuk menangkap
          bagaimana Anda menggunakan dan berinteraksi dengan situs web kami melalui metrik
          perilaku, heatmap, dan session replay guna meningkatkan serta memasarkan produk
          dan layanan kami. Data penggunaan situs dikumpulkan menggunakan cookie pihak
          pertama dan pihak ketiga serta teknologi pelacakan lainnya untuk menentukan
          popularitas produk/layanan dan aktivitas online. Selain itu, kami menggunakan
          informasi ini untuk optimasi situs, keperluan keamanan/fraud, dan periklanan.
          Untuk informasi lebih lanjut tentang bagaimana Microsoft mengumpulkan dan
          menggunakan data Anda, kunjungi{" "}
          <a
            href="https://privacy.microsoft.com/privacystatement"
            target="_blank"
            rel="noopener noreferrer"
            className="text-primary underline-offset-2 hover:underline"
          >
            Pernyataan Privasi Microsoft
          </a>
          .
        </Callout>
        <h3 className="mt-6 text-lg font-semibold text-foreground">
          Data yang kami kumpulkan
        </h3>
        <ul className="list-disc space-y-2 pl-5 text-sm text-muted-foreground">
          <li>
            <strong className="text-foreground">Microsoft Clarity:</strong> session replay,
            heatmap, klik, scroll, dan metrik perilaku lainnya — digunakan untuk memperbaiki
            pengalaman pengguna dan menemukan friksi pada antarmuka.
          </li>
          <li>
            <strong className="text-foreground">Google Analytics:</strong> jumlah pengunjung,
            halaman yang dilihat, sumber traffic, perangkat, dan lokasi geografis pada
            tingkat agregat — digunakan untuk mengukur efektivitas konten dan kampanye.
          </li>
          <li>
            Kami tidak menjual data pribadi Anda kepada pihak ketiga.
          </li>
        </ul>
        <h3 className="mt-6 text-lg font-semibold text-foreground">Hak Anda</h3>
        <p className="text-sm text-muted-foreground">
          Anda dapat menonaktifkan cookie pelacakan melalui pengaturan browser Anda. Jika
          Anda ingin meminta penghapusan data, hubungi kami melalui informasi kontak yang
          tertera di situs.
        </p>
      </article>
    </>
  );
}

function EnglishContent({ siteName }: { siteName: string }) {
  return (
    <>
      <article className="space-y-4">
        <SectionHeading index="01">Site Disclosure</SectionHeading>
        <p className="text-muted-foreground">
          We use <strong className="text-foreground">Microsoft Clarity</strong> and{" "}
          <strong className="text-foreground">Google Analytics</strong> to understand how
          visitors interact with this site so we can continually improve our products and
          services.
        </p>
        <Callout>
          We improve our products and advertising by using Microsoft Clarity to see how you
          use our website. By using our site, you agree that we and Microsoft can collect
          and use this data. Our{" "}
          <a
            href="https://privacy.microsoft.com/privacystatement"
            target="_blank"
            rel="noopener noreferrer"
            className="text-primary underline-offset-2 hover:underline"
          >
            Microsoft Privacy Statement
          </a>{" "}
          has more details.
        </Callout>
        <ul className="list-disc space-y-2 pl-5 text-sm text-muted-foreground">
          <li>We use Microsoft Clarity and Google Analytics.</li>
          <li>
            We capture behavioral data — such as clicks, scrolls, cursor movement, and
            session duration — for analytics and user-experience improvement.
          </li>
          <li>
            By using this site, users agree to data collection and use by {siteName} and
            Microsoft.
          </li>
        </ul>
      </article>

      <article className="space-y-4">
        <SectionHeading index="02">Privacy Policy</SectionHeading>
        <p className="text-muted-foreground">
          The following summarizes how we handle your data while using third-party analytics
          services on {siteName}.
        </p>
        <Callout>
          We partner with Microsoft Clarity and Microsoft Advertising to capture how you use
          and interact with our website through behavioral metrics, heatmaps, and session
          replay to improve and market our products/services. Website usage data is captured
          using first and third-party cookies and other tracking technologies to determine
          the popularity of products/services and online activity. Additionally, we use this
          information for site optimization, fraud/security purposes, and advertising. For
          more information about how Microsoft collects and uses your data, visit the{" "}
          <a
            href="https://privacy.microsoft.com/privacystatement"
            target="_blank"
            rel="noopener noreferrer"
            className="text-primary underline-offset-2 hover:underline"
          >
            Microsoft Privacy Statement
          </a>
          .
        </Callout>
        <h3 className="mt-6 text-lg font-semibold text-foreground">
          Data we collect
        </h3>
        <ul className="list-disc space-y-2 pl-5 text-sm text-muted-foreground">
          <li>
            <strong className="text-foreground">Microsoft Clarity:</strong> session replays,
            heatmaps, clicks, scrolls, and other behavioral metrics — used to improve user
            experience and surface UI friction.
          </li>
          <li>
            <strong className="text-foreground">Google Analytics:</strong> visitor counts,
            page views, traffic sources, devices, and aggregated geographic location — used
            to measure content and campaign effectiveness.
          </li>
          <li>We do not sell your personal data to third parties.</li>
        </ul>
        <h3 className="mt-6 text-lg font-semibold text-foreground">Your rights</h3>
        <p className="text-sm text-muted-foreground">
          You can disable tracking cookies via your browser settings. To request deletion of
          your data, contact us using the information published on the site.
        </p>
      </article>
    </>
  );
}
